import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import { APP_CONFIG } from '../config/app';

const AnimationContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  pointer-events: none; /* Allows clicking through */
  overflow: hidden;
  z-index: 1000; /* High z-index to appear over other elements */
`;

const CoinImage = styled.img`
  position: absolute;
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  top: ${props => props.initialTop}px;
  left: ${props => props.left}%;
  transform: rotate(${props => props.rotation}deg);
  animation: ${props => `coinFall ${props.duration}s ease-in ${props.delay}s forwards`};
  opacity: 0;
  
  @keyframes coinFall {
    0% {
      opacity: 1;
      top: ${props => props.initialTop}px;
      transform: rotate(${props => props.rotation}deg);
    }
    20% {
      opacity: 1;
    }
    95% {
      opacity: 0.8;
    }
    100% {
      opacity: 0;
      top: ${props => props.finalTop}px;
      transform: rotate(${props => props.rotation + 360}deg);
    }
  }
`;

const CoinDropAnimation = ({ isActive, onAnimationComplete }) => {
  const [coins, setCoins] = useState([]);
  const animationTriggeredRef = useRef(false);
  const timerRef = useRef(null);
  
  // Reset animation when component mounts/unmounts to prevent unexpected animations
  useEffect(() => {
    // Reset on mount
    animationTriggeredRef.current = false;
    setCoins([]);
    
    // Reset on unmount
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      animationTriggeredRef.current = false;
      setCoins([]);
    };
  }, []);
  
  // Store last active state to detect changes
  const lastActiveRef = useRef(false);
  
  useEffect(() => {
    // Only trigger animation on transition from inactive to active
    const isNewActivation = isActive && !lastActiveRef.current;
    lastActiveRef.current = isActive;
    
    // Only create coins if this is a new activation and we haven't already triggered an animation
    if (isNewActivation && !animationTriggeredRef.current) {
      // Mark as triggered so it doesn't run again until reset
      animationTriggeredRef.current = true;
      
      const newCoins = [];
      // Create 15-25 coins
      const coinCount = Math.floor(Math.random() * 11) + 15;
      
      for (let i = 0; i < coinCount; i++) {
        newCoins.push({
          id: `coin-${Date.now()}-${i}`,
          size: Math.floor(Math.random() * 20) + 15, // 15-35px
          left: Math.random() * 100, // 0-100%
          initialTop: -50, // Start above viewport
          finalTop: window.innerHeight + 50, // End below viewport
          rotation: Math.floor(Math.random() * 360),
          duration: 1.5 + Math.random() * 1.5, // 1.5-3s
          delay: Math.random() * 0.8, // Staggered start up to 0.8s
        });
      }
      
      setCoins(newCoins);
      
      // Calculate when all coins will have finished animating
      const maxDuration = Math.max(...newCoins.map(coin => coin.duration + coin.delay)) * 1000 + 200; // Add a small buffer
      
      // Clear any existing timers to avoid multiple timers
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      
      // Clean up after animation completes
      timerRef.current = setTimeout(() => {
        setCoins([]);
        if (onAnimationComplete) onAnimationComplete();
        // Reset the trigger flag after animation completes and a small delay
        setTimeout(() => {
          animationTriggeredRef.current = false;
        }, 300);
      }, maxDuration);
    } else if (!isActive) {
      // If we're deactivating, reset the animation state
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      setCoins([]);
      // Allow a new activation after navigating back
      animationTriggeredRef.current = false;
    }
    
    // Clean up function
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [isActive, onAnimationComplete]);
  
  // Only render when we have coins
  if (coins.length === 0) return null;
  
  return (
    <AnimationContainer>
      {coins.map((coin) => (
        <CoinImage 
          key={coin.id}
          src={APP_CONFIG.PRIMARY_COIN_IMAGE_PATH}
          alt="coin"
          size={coin.size}
          left={coin.left}
          initialTop={coin.initialTop}
          finalTop={coin.finalTop}
          rotation={coin.rotation}
          duration={coin.duration}
          delay={coin.delay}
        />
      ))}
    </AnimationContainer>
  );
};

export default CoinDropAnimation;
